import { ThemeUICSSObject } from 'theme-ui';
import { focusOutline, responsive, spacing } from '../../../../util';
import animations from './animations';

const chatMarkdownContent: ThemeUICSSObject = {
  color: 'text',
  fontFamily: 'body',
  lineHeight: 'body',
  whiteSpace: 'normal',
  width: '100%',

  'h1, h2, h3, h4, h5, h6': {
    color: 'palette.primary.main',
    fontWeight: 700,
    letterSpacing: 0,
    lineHeight: '1.35',
    m: 0,
  },

  h1: {
    fontSize: '25px',
    mb: '24px',
    mt: '13px',
    pb: '2px',
  },

  h2: {
    fontSize: '22px',
    mb: '23px',
  },

  h3: {
    fontSize: '21px',
    mb: '10px',
    mt: '16px',
  },

  h4: {
    fontSize: '20px',
    mb: '10px',
    mt: '16px',
  },

  h5: {
    fontSize: '19px',
    mb: '10px',
    mt: '16px',
  },

  h6: {
    fontSize: '18px',
    mb: '10px',
    mt: '16px',
  },

  p: {
    mb: '8px',
    mt: '20px',
  },

  table: {
    display: 'table',
    maxWidth: '100%',
    overflow: 'visible',
    tableLayout: 'auto',
    variant: 'layout.common.tables.secondary',
    whiteSpace: 'normal',
    width: '100%',

    thead: {
      zIndex: 0,
    },
  },

  'th, td': {
    display: 'table-cell',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    wordBreak: 'normal',
  },
};

export const semanticSearch: ThemeUICSSObject = {
  execution: {
    documentPage: {
      padding: 4,
      //maxWidth: 800,
      width: '100%',
      margin: '0 auto',
      fontFamily: 'body',
      lineHeight: 'body',
      color: 'text',

      h1: {
        fontSize: '25px',
        mb: '24px',
        mt: '13px',
        borderColor: 'muted',
        pb: '2px',
        color: 'primary',
      },
      h2: {
        fontSize: '22px',
        mb: '23px',
        color: 'primary',
      },
      h3: {
        fontSize: '21px',
        mb: '10px',
        mt: '16px',
        color: 'pallete.text.title',
      },
      h4: {
        fontSize: '20px',
        mb: '10px',
        mt: '16px',
        color: 'pallete.text.title',
      },
      h5: {
        fontSize: '19px',
        mb: '10px',
        mt: '16px',
        color: 'pallete.text.title',
      },
      h6: {
        fontSize: '18px',
        mb: '10px',
        mt: '16px',
        color: 'pallete.text.title',
      },
      p: {
        mb: '8px',
        mt: '20px',
      },
      table: {
        variant: 'layout.common.tables.secondary',
        thead: {
          zIndex: 0,
        },
      },
    },
    background: 'palette.background.default',

    '.chat_history': {
      margin: '50px 0 90px 0',
    },

    pre: {
      m: 0,
    },

    '.sem_search__system_message': {
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
    },

    streamBlock: {
      animation: `${animations.fadeIn} 0.2s`,
      borderRadius: '5px',
      padding: '5px 10px',
      background: '#d8e3e7',
      color: 'black',
      listStyle: 'disc',
    },
    streamBlocks: {
      display: 'flex',
      flexDirection: 'column',
      gap: 3,
      padding: '16px 24px 0 24px',
    },

    '.sem_search__stream_blocks': {
      variant: 'layout.common.ai.semanticSearch.execution.streamBlocks',
    },

    '.sem_search__stream_block': {
      variant: 'layout.common.ai.semanticSearch.execution.streamBlock',
    },

    '.sem_search__thumbs': {
      display: 'flex',
      justifyContent: 'end',
      gap: 2,
      position: 'absolute',
      bottom: 0,
      right: 0,

      '.unselected': {
        color: '#c2c2c2',
      },
      '.selected': {
        strokeWidth: '15px',
      },
      '.no_feedback': {
        color: 'gray',
      },
    },

    ellipsis: { padding: '0 24px 32px 24px', fontWeight: 'bold' },

    '.ellipsis': {
      variant: 'layout.common.ai.semanticSearch.execution.ellipsis',
    },

    '.filters__container': {
      ' > div': {
        display: 'flex',
        ' > .filter_button:last-child': {
          borderRadius: 'panel',
          height: '34px',
          width: '34px',
          flexShrink: 0,
          background: 'transparent',
          border: 'none',
          opacity: 0,
          transition: 'opacity 400ms',
          marginLeft: 'auto',
        },
      },
      paddingTop: '10px',
      '.filter__input': {
        paddingRight: '7px',
      },
    },
    '.filled_filters': {
      ' > div': {
        display: 'flex',
        ' > .filter_button:last-child': {
          borderRadius: 'panel',
          height: '34px',
          width: '34px',
          flexShrink: 0,
          background: 'transparent',
          border: 'none',
          opacity: 1,
          marginLeft: 'auto',
        },
      },
    },
    '.search_controls': {
      alignItems: 'center',
      justifyContent: 'center',
      display: 'flex',
      gap: 0,
      mx: 'auto',
      background: 'palette.background.paper',
      border: '1px solid',
      borderColor: 'palette.border.section',
      borderRadius: 'panel',
      boxShadow: '0px 2px 6px #0002',
      width: '100%',
      p: spacing(3),

      '&:focus-within': focusOutline,

      '& > *:focus': {
        outline: 'none',
      },
      '.filter_button_box': {
        position: 'relative',
      },
      '.applied_filters_label': {
        position: 'absolute',
        bottom: '21px',
        left: '20px',
        minWidth: '10px',
        maxHeight: '10px',
        border: '2px solid white',
        borderRadius: '100%',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'palette.error.main',
      },
    },

    '.SearchButton': {
      borderRadius: 'panel',
      height: '34px',
      width: '34px',
      flexShrink: 0,
      background: 'transparent',
      border: 'none',
      color: 'palette.text.primary',
      ':disabled': {
        background: 'transparent',
      },
    },
    '.FilterButton': {
      borderRadius: 'panel',
      height: '34px',
      width: '34px',
      flexShrink: 0,
      background: 'transparent',
      border: 'none',
      color: 'palette.text.primary',
      '&.open': {
        color: 'palette.primary.main',
      },
    },
    '.references__box': {
      display: 'flex',
      alignItems: 'center',
      gap: spacing(5),
    },
    '.searchTextArea': {
      minHeight: '34px',
      height: '34px',
      resize: 'none',
      py: spacing(2),
      background: 'transparent',
      border: 'none',
    },
    '.references_item': {
      maxWidth: '180px',
      whiteSpace: 'nowrap',
      zIndex: 0,

      '.button__content': {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: '180px',
      },
    },
    '&.mode_none': {
      flexDirection: 'column',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      gap: spacing(5),

      '.search_header_container': {
        fontSize: '50px',
        maxWidth: '50vw',
        textAlign: 'left',
        display: 'flex',
        flexDirection: 'column',
        gap: spacing(5),
      },

      '.search_section_container': {
        mx: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: spacing(3),
        zIndex: 1,
        background: 'palette.background.default',
        boxShadow: '0 10px 10px #f8f4f4',
        width: responsive({
          0: 'calc(100%)',
          3: 'calc(75vw + 50px)',
          5: 'calc(50vw + 50px)',
        }),
      },
    },
    '&.mode_chat': {
      h1: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },

      backgroundColor: 'white',
      width: responsive({
        0: 'calc(100%)',
        3: 'calc(75vw + 2px)',
        5: 'min(950px, calc(60vw + 2px))',
      }),
      margin: ['auto', '32px auto'],
      border: '1px solid #ccc',

      '.search_section_container': {
        zIndex: 1,
        position: 'fixed',
        bottom: '0',
        transform: 'translateX(-1px)',
        borderBottom: '4px solid #f2f2f2',
        width: responsive({
          0: 'calc(100%)',
          3: 'calc(75vw + 2px)',
          5: 'min(950px, calc(60vw + 2px))',
        }),
        background: 'white',
        textarea: { width: 'calc(100%)' },

        '& > div': {
          padding: '4px 32px',
          borderLeft: '1px solid #ccc',
          borderRight: '1px solid #ccc',
          borderBottom: '1px solid #ccc',
          width: 'calc(100%)',
          background: 'white',
        },
      },

      '.search_header_container': {
        position: 'fixed',
        zIndex: 1,
        top: '0',
        transform: 'translateX(-1px)',
        borderTop: '4px solid #f2f2f2',
        padding: '0',
        width: responsive({
          0: 'calc(100%)',
          3: 'calc(75vw + 2px)',
          5: 'min(950px, calc(60vw + 2px))',
        }),

        h1: {
          padding: '32px',
          pt: '20px',
          pb: 0,
          borderLeft: '1px solid #ccc',
          borderRight: '1px solid #ccc',
          borderTop: '1px solid #ccc',
          width: 'calc(100%)',
          background: 'white',
        },

        p: {
          background: 'palette.background.paper',
          padding: '32px',
          pb: '20px',
          pt: 0,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          width: 'calc(100% - 2px)',
          ml: '1px',
        },
      },

      '.messages_box': {
        display: 'flex',
        flexDirection: 'column',
        fontSize: '22px',
        height: '82vh',
        marginBottom: '8%',
        minHeight: 0,
        overflow: 'hidden',
        paddingTop: '7%',

        '.chatHistoryVirtualScroller': {
          flex: '1 1 auto',
          height: 'auto !important',
          minHeight: 0,
          overflow: 'auto',
        },

        '.chat_history': {
          margin: 0,
        },

        '.history_message.system .history_message__content.message__body.markdown .sem_search__system_message .message__body.markdown [data-variant="layout.common.markdown"]':
          chatMarkdownContent,
      },
      '.history__message': {
        borderRadius: 'panel',
        margin: '15px',
        padding: '18px',
        border: '1px',

        //boxShadow: 'rgba(50, 50, 93, 0.25) 2px 2px 4px 1px',
        '&.user': {
          borderRadius: 'chatMessage',
          borderTopRightRadius: '0px',
          backgroundColor: 'rgb(108 166 203)', //rgb(117 163 192)', //rgb(92, 143, 174)',
          color: 'white',
          boxShadow: 'rgba(50, 50, 93, 0.15) 1px 1px 2px 1px',
          border: '1px solid rgb(113 145 193)',
        },
        '&.system': {
          borderRadius: 'chatMessage',
          borderTopLeftRadius: '0px',
          backgroundColor: '#00bcff14',
          boxShadow: 'rgba(50, 50, 93, 0.15) 1px 1px 2px 1px',
          border: '1px solid rgb(0 28 38 / 18%)',
        },
      },
      '.history': {
        animation: `${animations.fadeInFromBottom} 0.3s`,
        padding: '100px 32px 80px 32px',
        border: 'none',
        height: 'unset',
      },
    },
    '&.mode_list': {
      p: {
        '&[name="semSerDescription"]': {
          padding: '0px 23vw',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          width: 'calc(100% - 2px)',
          marginLeft: '1px',
        },
      },
      display: 'block',
      gap: spacing(5),
      px: responsive({
        0: 5,
        3: 0,
      }),
      '.search_header_container': {
        marginTop: '7px',
        h1: {
          padding: '0 23vw',
          fontSize: '20px',
        },
      },
      '.filters__container': {
        padding: '0 25px',
        paddingTop: '10px',
      },

      '.search_section_container': {
        paddingTop: '47px',
        mx: 'auto',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1,
        boxShadow: '0 10px 10px #f8f4f4',
        width: responsive({
          0: 'calc(100%)',
          3: 'calc(75vw + 50px)',
          5: 'calc(50vw + 50px)',
        }),
        background: 'palette.background.default',
        position: 'sticky',
        top: 0,
        py: spacing(5),
        gap: spacing(5),
      },

      '.list_results': {
        animation: `${animations.fadeInFromTop} 0.3s`,

        height: '100%',
        width: responsive({ 0: '100%', 3: '75vw', 5: '50vw' }),
        mx: 'auto',
        pt: spacing(3),
        display: 'flex',
        flexDirection: 'column',
        gap: spacing(6),
      },
      '.list_item': {
        borderRadius: 'panel',
        padding: spacing(7),
        backgroundColor: 'palette.background.paper',
        boxShadow: '0px 4px 6px #0002',
        border: '1px solid',
        borderColor: 'palette.border.ghost',
      },
      '.references_container': {
        borderTop: '1px solid',
        borderTopColor: 'palette.border.article',
        display: 'flex',
        gap: spacing(6),
        pt: spacing(5),
      },
    },

    '.autogrow_textarea': {
      maxHeight: '20vh',
      width: '100%',
    },

    refsAccordion: {
      variant: 'layout.common.components.accordion.panels',
      border: (theme) => `1px solid ${theme.palette.border.ghost}`,

      refButton: {
        variant: 'buttons.paper',
      },

      '& .accordion__item__button': {
        userSelect: 'unset',
        position: 'sticky',
        top: 0,
      },
    },
  },
};
